import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function DuplicateInvoiceDialog({ inv, onClose }) {
  const navigate = useNavigate();
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!inv) return;
    setNumber("");
    api.get("/invoices/next-number", { params: { service_date: inv.service_date } }).then((r) => setNumber(r.data.number || "")).catch(() => {});
  }, [inv]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/duplicate`, { number: number.trim() });
      toast.success(`Invoice ${data.number} created as a copy of ${inv.number}`);
      onClose();
      navigate(`/invoices/${data.id}`);
    } catch (err) { toast.error(formatError(err)); }
    setBusy(false);
  };

  return (
    <Dialog open={!!inv} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card" data-testid="duplicate-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Duplicate invoice {inv?.number}</DialogTitle>
          <DialogDescription>Makes an exact copy (customer, lines, totals, notes, page 2) under a new invoice number. Paid status isn't copied.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="dup-number">New invoice number</Label>
            <Input id="dup-number" value={number} onChange={(e) => setNumber(e.target.value)} required maxLength={30} className="font-mono" data-testid="duplicate-invoice-number" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} data-testid="duplicate-invoice-cancel">Cancel</Button>
            <Button type="submit" disabled={busy || !number.trim()} data-testid="duplicate-invoice-save">{busy ? <Loader2 size={15} className="animate-spin" /> : <Copy size={15} />} Duplicate</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
